import React, { useEffect, useRef, useState } from "react";
import { getPaymentType, PAYMENT_TYPES } from "./OfferLanguage";
import {
  formatCurrency,
  parsePriceInput,
  roundPrice,
  toPriceInputValue,
} from "./utils/priceInput";
import "./OfferServiceQuickRow.css";

const PAYMENT_OPTIONS = [
  { type: PAYMENT_TYPES.ONE_TIME, label: "One-time", icon: "fas fa-receipt" },
  { type: PAYMENT_TYPES.RECURRING, label: "Monthly", icon: "fas fa-rotate" },
  {
    type: PAYMENT_TYPES.PER_APPOINTMENT,
    label: "Per appt.",
    icon: "fas fa-calendar-check",
  },
];

const QUICK_STEPS = [-10, -5, 5, 10];

/**
 * One selected service inside the offer editor.
 *
 * Small per-customer deviations (price, billing mode, label) are handled right
 * here so the full customizer modal is only needed for microservices.
 */
const OfferServiceQuickRow = ({
  service,
  override = {},
  hasCustomizations = false,
  priceSuffix,
  onChangeOverride,
  onResetOverride,
  onOpenCustomizer,
  onRemove,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [priceDraft, setPriceDraft] = useState("");
  const priceInputRef = useRef(null);

  const catalogPrice = service.price || 0;
  const effectivePrice =
    override.price !== undefined ? override.price : catalogPrice;
  const discountPercent = service.discountPercent || 0;
  const finalPrice = effectivePrice * (1 - discountPercent / 100);

  const paymentType = getPaymentType({
    ...service,
    paymentType: override.paymentType,
    isRecurring:
      override.paymentType !== undefined
        ? undefined
        : override.isRecurring !== undefined
        ? override.isRecurring
        : service.isRecurring,
  });

  const paymentOption = PAYMENT_OPTIONS.find(
    (option) => option.type === paymentType
  );
  const priceOverridden = override.price !== undefined;
  const titleOverridden = !!override.title;
  const paymentOverridden =
    override.paymentType !== undefined || override.isRecurring !== undefined;
  const isModified = priceOverridden || titleOverridden || paymentOverridden;

  useEffect(() => {
    if (!isOpen) return;
    setPriceDraft(toPriceInputValue(effectivePrice));
    // Jumping straight into the field keeps this a one-click adjustment.
    const frame = requestAnimationFrame(() => {
      priceInputRef.current?.focus();
      priceInputRef.current?.select();
    });
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const commitPrice = (rawValue) => {
    const parsed = parsePriceInput(rawValue);
    if (parsed === null) {
      setPriceDraft(toPriceInputValue(effectivePrice));
      return;
    }
    // Matching the catalog again means there is nothing left to override.
    onChangeOverride(service.id, {
      price: parsed === catalogPrice ? undefined : parsed,
    });
    setPriceDraft(toPriceInputValue(parsed));
  };

  const applyStep = (percent) => {
    // The visible draft wins, so stepping right after typing stays predictable.
    const current = parsePriceInput(priceDraft) ?? effectivePrice;
    const next = roundPrice(current * (1 + percent / 100));
    setPriceDraft(toPriceInputValue(next));
    onChangeOverride(service.id, {
      price: next === catalogPrice ? undefined : next,
    });
  };

  const handlePriceKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitPrice(priceDraft);
      setIsOpen(false);
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setPriceDraft(toPriceInputValue(effectivePrice));
      setIsOpen(false);
    }
  };

  return (
    <li
      className={`service-row${isModified ? " service-row--modified" : ""}${
        isOpen ? " service-row--open" : ""
      }`}
    >
      <div className="service-row__main">
        <div className="service-row__text">
          <span className="service-row__title">
            {override.title || service.title}
          </span>
          <div className="service-row__meta">
            {paymentType !== PAYMENT_TYPES.ONE_TIME && paymentOption && (
              <span
                className={`service-row__tag${
                  paymentOverridden ? " service-row__tag--accent" : ""
                }`}
              >
                <i className={paymentOption.icon} aria-hidden="true"></i>
                {paymentOption.label}
              </span>
            )}
            {discountPercent > 0 && (
              <span className="service-row__tag service-row__tag--discount">
                −{discountPercent}%
              </span>
            )}
            {isModified && (
              <span className="service-row__tag service-row__tag--accent">
                <i className="fas fa-pen" aria-hidden="true"></i>
                Adjusted
              </span>
            )}
          </div>
        </div>

        <button
          type="button"
          className="service-row__price"
          onClick={() => setIsOpen((open) => !open)}
          title="Adjust price for this offer"
        >
          {priceOverridden && (
            <span className="service-row__price-was">
              {formatCurrency(catalogPrice * (1 - discountPercent / 100))}
            </span>
          )}
          <span className="service-row__price-value">
            {formatCurrency(finalPrice)}
            {priceSuffix && (
              <span className="service-row__price-suffix">{priceSuffix}</span>
            )}
          </span>
        </button>

        <div className="service-row__actions">
          <button
            type="button"
            className={`service-row__action${
              isOpen ? " service-row__action--active" : ""
            }`}
            onClick={() => setIsOpen((open) => !open)}
            title="Quick adjustments"
          >
            <i className="fas fa-sliders" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            className={`service-row__action${
              hasCustomizations ? " service-row__action--dot" : ""
            }`}
            onClick={() => onOpenCustomizer(service)}
            title={
              hasCustomizations
                ? "Microservices (customized)"
                : "Customize microservices for this offer"
            }
          >
            <i className="fas fa-gear" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            className="service-row__action service-row__action--danger"
            onClick={() => onRemove(service.id)}
            title="Remove from offer"
          >
            <i className="fas fa-xmark" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="service-row__panel">
          <div className="service-row__field">
            <label className="service-row__field-label">
              Price for this offer
            </label>
            <div className="service-row__price-input">
              <input
                ref={priceInputRef}
                type="text"
                inputMode="decimal"
                value={priceDraft}
                onChange={(event) => setPriceDraft(event.target.value)}
                onBlur={() => commitPrice(priceDraft)}
                onKeyDown={handlePriceKeyDown}
              />
              <span className="service-row__currency">€</span>
            </div>
            <div className="service-row__steps">
              {QUICK_STEPS.map((step) => (
                <button
                  key={step}
                  type="button"
                  className="service-row__chip"
                  onClick={() => applyStep(step)}
                  title={`${step > 0 ? "Increase" : "Reduce"} by ${Math.abs(
                    step
                  )}%`}
                >
                  {step > 0 ? `+${step}%` : `−${Math.abs(step)}%`}
                </button>
              ))}
              {isModified && (
                <button
                  type="button"
                  className="service-row__chip service-row__chip--reset"
                  onClick={() => {
                    onResetOverride(service.id);
                    setPriceDraft(toPriceInputValue(catalogPrice));
                  }}
                  title="Drop all adjustments and use the catalog service again"
                >
                  <i className="fas fa-rotate-left" aria-hidden="true"></i>
                  Reset
                </button>
              )}
            </div>
          </div>

          <div className="service-row__field">
            <label className="service-row__field-label">Billing</label>
            <div className="service-row__segmented">
              {PAYMENT_OPTIONS.map((option) => (
                <button
                  key={option.type}
                  type="button"
                  className={`service-row__segment${
                    paymentType === option.type
                      ? " service-row__segment--active"
                      : ""
                  }`}
                  onClick={() =>
                    onChangeOverride(service.id, {
                      paymentType:
                        option.type === getPaymentType(service)
                          ? undefined
                          : option.type,
                      isRecurring: undefined,
                    })
                  }
                >
                  <i className={option.icon} aria-hidden="true"></i>
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="service-row__field">
            <label className="service-row__field-label">
              Label in this offer
            </label>
            <input
              type="text"
              className="service-row__text-input"
              value={override.title || ""}
              placeholder={service.title}
              onChange={(event) =>
                onChangeOverride(service.id, {
                  title: event.target.value.trim() ? event.target.value : undefined,
                })
              }
            />
          </div>

          <div className="service-row__footnote">
            {priceOverridden
              ? `Catalog price ${formatCurrency(catalogPrice)} — only this offer is affected.`
              : "Adjustments apply to this offer only, the catalog service stays untouched."}
            {discountPercent > 0 &&
              ` Service discount of ${discountPercent}% leads to ${formatCurrency(
                finalPrice
              )}.`}
          </div>
        </div>
      )}
    </li>
  );
};

export default OfferServiceQuickRow;
